import { X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { TONE_DOT, type StatusTone } from '~/components/ui/status-chip/tones';

interface ChipProps {
  /** The mono tag, e.g. `default` or an environment code. */
  label: string;
  tone?: StatusTone;
  size?: 'sm';
  dot?: boolean;
  selected?: boolean;
  /** Makes the chip a `role="tab"`. */
  onClick?: () => void;
  /** Adds a `×` that removes what the chip stands for. */
  onRemove?: () => void;
  /** Accessible name of the `×`; defaults to `Remove {label}`. */
  removeLabel?: string;
  /** Replaces the label as the accessible name. */
  ariaLabel?: string;
  testId?: string;
  className?: string;
}

const SURFACE: Record<'base' | 'brand', string> = {
  base: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-300',
  brand:
    'border-brand-300 bg-brand-50 text-gray-700 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-gray-300',
};

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

export function Chip({
  label,
  tone = 'neutral',
  dot = false,
  selected = false,
  onClick,
  onRemove,
  removeLabel,
  ariaLabel,
  testId,
  className,
}: ChipProps) {
  const name = ariaLabel ?? label;
  const body = (
    <>
      {dot && (
        <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${TONE_DOT[tone]}`} />
      )}
      <span className="font-mono">{label}</span>
    </>
  );
  const chipClass = twMerge(
    'inline-flex h-5.5 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-xs',
    tone === 'brand' ? SURFACE.brand : SURFACE.base,
    selected && 'border-brand-500 text-gray-800 dark:border-brand-400 dark:text-white/90',
    className,
  );

  if (!onClick && !onRemove) {
    return (
      <span role="img" aria-label={name} data-testid={testId} className={chipClass}>
        {body}
      </span>
    );
  }

  return (
    <span data-testid={testId} className={chipClass}>
      {onClick ? (
        <button
          type="button"
          role="tab"
          aria-selected={selected}
          aria-label={name}
          onClick={onClick}
          className={`inline-flex items-center gap-1.5 rounded-full ${FOCUS}`}
        >
          {body}
        </button>
      ) : (
        <span role="img" aria-label={name} className="inline-flex items-center gap-1.5">
          {body}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel ?? `Remove ${label}`}
          onClick={onRemove}
          className={`rounded-full text-gray-500 hover:text-error-600 dark:text-gray-400 ${FOCUS}`}
        >
          <X className="size-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
